import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';

// Eigene Schulungen (Mitarbeiter-Tab): hochgeladenes Dokument zur Kenntnisnahme

export type CustomTrainingType = 'Acknowledgement';

export interface CustomTrainingConfirmation {
  id: string;
  createdAt: string;
  employeeId: string;
  fileId: string;
  isStale: boolean;
  staleReason?: string | null;
}

export interface CustomTraining {
  id: string;
  createdAt: string;
  updatedAt: string;
  type: CustomTrainingType;
  title: string;
  description?: string | null;
  renewalMonths?: number | null;
  fileId: string;
  file?: { id: string; name: string; size: number } | null;
  confirmations: CustomTrainingConfirmation[];
}

const CUSTOM_TRAINING_FIELDS = gql`
  fragment CustomTrainingFields on CustomTraining {
    id
    createdAt
    updatedAt
    type
    title
    description
    renewalMonths
    fileId
    file {
      id
      name
      size
    }
    confirmations {
      id
      createdAt
      employeeId
      fileId
      isStale
      staleReason
    }
  }
`;

const GET_CUSTOM_TRAININGS = gql`
  ${CUSTOM_TRAINING_FIELDS}
  query CustomTrainings {
    customTrainings {
      ...CustomTrainingFields
    }
  }
`;

export function useCustomTrainingsQuery(skip = false) {
  return useQuery<{ customTrainings: CustomTraining[] }>(GET_CUSTOM_TRAININGS, {
    fetchPolicy: 'cache-and-network',
    skip,
  });
}

const REFETCH = { refetchQueries: ['CustomTrainings'], awaitRefetchQueries: true };
// multipart upload through apollo-upload-client (see useFileUploadMutation)
const UPLOAD_CONTEXT = { headers: { 'apollo-require-preflight': true } };

const CREATE_CUSTOM_TRAINING = gql`
  ${CUSTOM_TRAINING_FIELDS}
  mutation CreateCustomTraining(
    $title: String!
    $description: String
    $type: CustomTrainingType
    $renewalMonths: Int
    $file: Upload!
    $size: Int!
  ) {
    createCustomTraining(
      title: $title
      description: $description
      type: $type
      renewalMonths: $renewalMonths
      file: $file
      size: $size
    ) {
      ...CustomTrainingFields
    }
  }
`;

export interface CreateCustomTrainingInput {
  title: string;
  description?: string | null;
  type?: CustomTrainingType;
  renewalMonths?: number | null;
  file: File;
  size: number;
}

export function useCreateCustomTrainingMutation() {
  return useMutation<{ createCustomTraining: CustomTraining }, CreateCustomTrainingInput>(CREATE_CUSTOM_TRAINING, {
    ...REFETCH,
    context: UPLOAD_CONTEXT,
  });
}

const UPDATE_CUSTOM_TRAINING = gql`
  ${CUSTOM_TRAINING_FIELDS}
  mutation UpdateCustomTraining(
    $id: String!
    $title: String
    $description: String
    $renewalMonths: Int
    $file: Upload
    $size: Int
  ) {
    updateCustomTraining(
      id: $id
      title: $title
      description: $description
      renewalMonths: $renewalMonths
      file: $file
      size: $size
    ) {
      ...CustomTrainingFields
    }
  }
`;

export interface UpdateCustomTrainingInput {
  id: string;
  title?: string;
  description?: string | null;
  // null = einmalig, undefined = unverändert
  renewalMonths?: number | null;
  file?: File;
  size?: number;
}

export function useUpdateCustomTrainingMutation() {
  return useMutation<{ updateCustomTraining: CustomTraining }, UpdateCustomTrainingInput>(UPDATE_CUSTOM_TRAINING, {
    ...REFETCH,
    context: UPLOAD_CONTEXT,
  });
}

const ARCHIVE_CUSTOM_TRAINING = gql`
  mutation ArchiveCustomTraining($id: String!) {
    archiveCustomTraining(id: $id) {
      id
      archivedAt
    }
  }
`;

export function useArchiveCustomTrainingMutation() {
  return useMutation<{ archiveCustomTraining: { id: string } }, { id: string }>(ARCHIVE_CUSTOM_TRAINING, REFETCH);
}

const RESET_EMPLOYEE_CUSTOM_TRAINING = gql`
  mutation ResetEmployeeCustomTraining($employeeId: String!, $customTrainingId: String!) {
    resetEmployeeCustomTraining(employeeId: $employeeId, customTrainingId: $customTrainingId)
  }
`;

export function useResetEmployeeCustomTrainingMutation() {
  return useMutation<{ resetEmployeeCustomTraining: number }, { employeeId: string; customTrainingId: string }>(
    RESET_EMPLOYEE_CUSTOM_TRAINING,
    REFETCH
  );
}

const SEND_ALL_CUSTOM_TRAINING_EMAILS = gql`
  mutation SendAllCustomTrainingEmails($customTrainingId: String!, $baseUrl: String!) {
    sendAllCustomTrainingEmails(customTrainingId: $customTrainingId, baseUrl: $baseUrl) {
      sent
      skipped
      errors
    }
  }
`;

export function useSendAllCustomTrainingEmailsMutation() {
  return useMutation<
    { sendAllCustomTrainingEmails: { sent: number; skipped: number; errors: string[] } },
    { customTrainingId: string; baseUrl: string }
  >(SEND_ALL_CUSTOM_TRAINING_EMAILS);
}

const GET_CUSTOM_TRAINING_SHARE_TOKEN = gql`
  query GetCustomTrainingShareToken($customTrainingId: String!) {
    getCustomTrainingShareToken(customTrainingId: $customTrainingId)
  }
`;

export function useCustomTrainingShareTokenQuery() {
  return useLazyQuery<{ getCustomTrainingShareToken: string }, { customTrainingId: string }>(
    GET_CUSTOM_TRAINING_SHARE_TOKEN
  );
}

// ---- anonymous share page ----

export interface CustomTrainingPublic {
  id: string;
  type: CustomTrainingType;
  title: string;
  description?: string | null;
  fileId: string;
  fileName: string;
  companyName?: string | null;
}

const GET_CUSTOM_TRAINING_BY_TOKEN = gql`
  query GetCustomTrainingByToken($token: String!) {
    getCustomTrainingByToken(token: $token) {
      id
      type
      title
      description
      fileId
      fileName
      companyName
    }
  }
`;

export function useCustomTrainingByTokenQuery(token: string | undefined, skip: boolean) {
  return useQuery<{ getCustomTrainingByToken: CustomTrainingPublic }, { token: string }>(GET_CUSTOM_TRAINING_BY_TOKEN, {
    variables: { token: token || '' },
    skip: skip || !token,
  });
}

const CONFIRM_CUSTOM_TRAINING = gql`
  mutation ConfirmCustomTraining($token: String!, $name: String!) {
    confirmCustomTraining(token: $token, name: $name) {
      id
      createdAt
    }
  }
`;

export function useConfirmCustomTrainingMutation() {
  return useMutation<{ confirmCustomTraining: { id: string; createdAt: string } }, { token: string; name: string }>(
    CONFIRM_CUSTOM_TRAINING
  );
}
